
import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/base/Button';
import { ServiceCardSkeleton } from '../../../components/base/SkeletonLoader';
import { AnimatedSection } from '../../../components/base/AnimatedSection';

export const ServicesSection: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  // Simular carregamento inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const services = [
    {
      id: 1,
      icon: 'ri-shield-check-line',
      title: 'PPF - Paint Protection Film',
      description: 'Proteção invisível contra riscos, pedras e intempéries. Mantém a pintura original intacta por anos.',
      features: ['Proteção contra riscos', 'Resistente a UV', 'Autorreparável', 'Garantia de 10 anos'],
      price: 'A partir de R$ 499,90',
      duration: 'Duração personalizada',
      image: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/0e7d90604ebfce818e03d5f77d6db2c8.jpeg'
    },
    {
      id: 2,
      icon: 'ri-drop-line',
      title: 'Vitrificação Cerâmica',
      description: 'Revestimento cerâmico que proporciona brilho intenso e proteção duradoura contra contaminantes.',
      features: ['Brilho intenso', 'Facilita limpeza', 'Proteção química', 'Durabilidade até 3 anos'],
      price: 'A partir de R$ 999,90',
      duration: '1-3 dias',
      image: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/cc350a9dee52dd0b92e27199ffc79e10.jpeg'
    },
    {
      id: 3,
      icon: 'ri-window-line',
      title: 'Película Automotiva',
      description: 'Controle térmico e proteção UV para maior conforto e privacidade no seu veículo.',
      features: ['Redução térmica 70%', 'Proteção UV 99%', 'Privacidade', 'Garantia de 5 anos'],
      price: 'A partir de R$ 999,90',
      duration: '6 horas',
      image: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/f0c1be97f8cdbe1ceb6fee7d321a781a.jpeg'
    },
    {
      id: 4,
      icon: 'ri-brush-line',
      title: 'Polimento',
      description: 'Correção de pintura profissional para remover riscos, hologramas e oxidação.',
      features: ['Remove micro riscos', 'Elimina hologramas', 'Restaura brilho', 'Preparação para proteção'],
      price: 'A partir de R$ 700',
      duration: '1 dia',
      image: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/d8dcc35c6b681be59120adc1f1ac81de.png'
    },
    {
      id: 5,
      icon: 'ri-heart-pulse-line',
      title: 'Higienização Completa',
      description: 'Limpeza profunda e sanitização do interior com produtos específicos e equipamentos profissionais.',
      features: ['Sanitização completa', 'Remove odores', 'Trata couro/tecido', 'Proteção antimicrobiana'],
      price: 'A partir de R$ 600',
      duration: '6 horas',
      image: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/643b655754711aa632aab00e8e4be88f.png'
    },
    {
      id: 6,
      icon: 'ri-water-flash-line',
      title: 'Lavação Detalhada',
      description: 'Lavagem premium com técnicas seguras, produtos de alta qualidade e acabamento impecável.',
      features: ['Técnica dos 2 baldes', 'Produtos premium', 'Detalhamento com pincel', 'Pneus e rodas'],
      price: 'A partir de R$ 259,90',
      duration: '1-2 horas',
      image: 'https://static.readdy.ai/image/23e6e82711d555609e11a3dec8506ccb/58470e714f37834c69557b24972b41de.jpeg'
    }
  ];

  const handleWhatsApp = (service: string) => {
    const message = `Olá! Gostaria de agendar o serviço: ${service}`;
    window.open(`https://wa.me/554888397243?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (isLoading) {
    return (
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4 animate-pulse"></div>
            <div className="h-4 bg-gray-300 rounded w-96 mx-auto animate-pulse"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 6 }).map((_, index) => (
              <ServiceCardSkeleton key={index} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="servicos" className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-4">
        <AnimatedSection animation="fadeIn" className="text-center mb-8 md:mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-montserrat font-bold text-[#0D0D0D] mb-4">
            Nossos <span className="text-[#C8A45D]">Serviços</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto px-4">
            Proteção e cuidado premium para o seu veículo com tecnologia de ponta e garantia Aura
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, index) => (
            <AnimatedSection
              key={service.id}
              animation="slideUp"
              delay={index * 100}
              className="group"
            >
              <div className="bg-white rounded-xl border border-gray-200 hover:border-[#C8A45D] transition-all duration-300 hover:scale-105 h-full flex flex-col overflow-hidden shadow-lg hover:shadow-xl">
                {/* Image */}
                <div className="relative h-40 md:h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute top-3 md:top-4 left-3 md:left-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-[#C8A45D] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-black text-lg md:text-xl`}></i>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6 flex-grow flex flex-col">
                  {/* Header */}
                  <div className="mb-3 md:mb-4">
                    <h3 className="text-[#0D0D0D] font-montserrat font-bold text-base md:text-lg group-hover:text-[#C8A45D] transition-colors duration-300 mb-2 leading-tight">
                      {service.title}
                    </h3>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-3">
                      <p className="text-[#C8A45D] font-semibold text-base md:text-lg">
                        {service.price}
                      </p>
                      <span className="text-gray-500 text-xs md:text-sm flex items-center">
                        <i className="ri-time-line mr-1"></i>
                        {service.duration}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 mb-3 md:mb-4 flex-grow text-sm leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="mb-4 md:mb-6">
                    <ul className="space-y-1 md:space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-xs md:text-sm text-gray-600">
                          <i className="ri-check-line text-[#C8A45D] mr-2 flex-shrink-0"></i>
                          <span className="leading-tight">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <div className="mt-auto">
                    <Button
                      onClick={() => handleWhatsApp(service.title)}
                      variant="primary"
                      size="sm"
                      icon="ri-whatsapp-line"
                      className="w-full bg-[#C8A45D] hover:bg-[#B8944D] text-black font-bold text-xs md:text-sm py-2 md:py-3 px-3 md:px-4"
                    >
                      Solicitar Orçamento
                    </Button>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection animation="fadeIn" delay={600} className="text-center mt-8 md:mt-12 px-4">
          <p className="text-gray-600 mb-4 md:mb-6 text-sm md:text-base">
            Todos os serviços incluem <strong className="text-[#C8A45D]">garantia Aura</strong> e atendimento personalizado
          </p>
          <Button
            onClick={() => window.open('https://wa.me/554888397243?text=Olá! Gostaria de conhecer todos os serviços da Aura Estética Automotiva.', '_blank')}
            size="lg"
            icon="ri-phone-line"
            className="bg-[#C8A45D] hover:bg-[#B8944D] text-black font-bold w-full sm:w-auto text-sm md:text-base py-3 md:py-4 px-6 md:px-8"
          >
            Falar com Especialista
          </Button>
        </AnimatedSection>
      </div>
    </section>
  );
};